import logger from '@/utils/logger';
import { AUTH0_CLAIMS_EMAIL_KEY, MAX_MAGIC_TOKEN_AGE_MS, STORAGE_KEYS } from './constants';
import { clearEmbedUrlSession, getStoredEmbedUrlSessionToken, isEmbedUrlSessionToken } from './embedUrlSession';
import { decodeJwt } from './tokenDecode';
import { notifyTokenChanged, setUserEmail, setUserId } from './tokenStorage';
import type { JwtPayload, MagicTokenResult } from './types';

// gloss
export const setMagicToken = (token: string | null | undefined) => {
  if (typeof window === 'undefined' || !token) return;

  let payload: JwtPayload;
  try {
    payload = decodeJwt(token);
  } catch (error) {
    logger.warn('Failed to decode magic token, skipping storage', { context: 'setMagicToken' });
    return;
  }
  const tokenExpiry = payload.exp ? payload.exp * 1000 : Infinity;
  const maxExpiry = Date.now() + MAX_MAGIC_TOKEN_AGE_MS;
  const expiresAt = Math.min(tokenExpiry, maxExpiry);

  if (!isEmbedUrlSessionToken(token)) clearEmbedUrlSession();
  localStorage.setItem(STORAGE_KEYS.MAGIC_TOKEN, token);
  localStorage.setItem(STORAGE_KEYS.MAGIC_TOKEN_EXPIRY, String(expiresAt));

  const email = (payload.email ?? payload[AUTH0_CLAIMS_EMAIL_KEY]) as string | undefined;
  if (email) {
    setUserId(email);
    setUserEmail(email);
  }

  notifyTokenChanged();
};

// gloss
export const getMagicToken = (): MagicTokenResult | null => {
  if (typeof window === 'undefined') return null;

  const token = localStorage.getItem(STORAGE_KEYS.MAGIC_TOKEN);
  const expiresAt = Number(localStorage.getItem(STORAGE_KEYS.MAGIC_TOKEN_EXPIRY));

  if (!token || !expiresAt) return null;

  if (Date.now() >= expiresAt) {
    clearMagicToken();
    return null;
  }

  return { token, expiresAt };
};

// gloss
export const clearMagicToken = () => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(STORAGE_KEYS.MAGIC_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.MAGIC_TOKEN_EXPIRY);
  notifyTokenChanged();
};

// gloss
export const getLiveEmbedUrlSessionToken = (): string | null =>
  getMagicToken() ? getStoredEmbedUrlSessionToken() : null;
